import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { BrandMark } from "@/components/BrandMark";
import {
  ArrowRight,
  BookOpenCheck,
  Users,
  Sparkles,
} from "lucide-react";

const MODULES = [
  {
    n: 1,
    title: "Your Food Truck Foundation",
    purpose: "Understand the food truck world before spending money.",
    lessons: ["Market Basics", "Permits & Rules"],
    tool: "Local Market Observation Guide",
  },
  {
    n: 2,
    title: "What to Sell & What It Costs",
    purpose: "Build a focused menu that actually makes money.",
    lessons: ["Recipe Testing & Costing", "Pricing & Inventory Basics"],
    tool: "Recipe Cost Calculator",
  },
  {
    n: 3,
    title: "Your Brand in the Wild",
    purpose: "Create a clear, recognizable brand customers trust.",
    lessons: [
      "Naming, Logo & Visual Identity",
      "Showing Up Where Customers Are",
    ],
    tool: "Brand Clarity Checklist",
  },
  {
    n: 4,
    title: "Life on the Truck",
    purpose: "Prepare for real service-day rhythms and operations.",
    lessons: [
      "Prep, Service & Cleanup Flow",
      "Staffing, Roles & Communication",
    ],
    tool: "Service Day Checklist",
  },
  {
    n: 5,
    title: "Money Without the Stress",
    purpose: "Manage finances with clarity instead of anxiety.",
    lessons: [
      "Budgets, Cash Flow & Forecasting",
      "Taxes, Insurance & Financial Protection",
    ],
    tool: "Cash Flow Tracker Template",
  },
];

const INCLUDED = [
  {
    testid: "included-modules",
    icon: <BookOpenCheck className="h-6 w-6" />,
    title: "5 Modules",
    description: "Idea to income, step by step",
  },
  {
    testid: "included-community",
    icon: <Users className="h-6 w-6" />,
    title: "Community Forum",
    description: "Real answers from real operators",
  },
  {
    testid: "included-copilot",
    icon: <Sparkles className="h-6 w-6" />,
    title: "Copilot Guidance",
    description: "Calm, personalized next steps",
  },
];

function ModuleCard({ n, title, purpose, lessons, tool }) {
  return (
    <article
      data-testid={`academy-module-${n}`}
      className="group relative rounded-2xl border border-slate-200 bg-white p-7 hover:border-[#E8650A]/40 hover:shadow-[0_10px_30px_-12px_rgba(232,101,10,0.18)] transition"
    >
      <div className="flex items-center justify-between">
        <span
          className="inline-flex h-9 min-w-9 px-2.5 items-center justify-center rounded-lg bg-[#E8650A] text-white text-sm font-bold tracking-wide"
          data-testid={`academy-module-${n}-badge`}
        >
          Module {n}
        </span>
        <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-slate-400">
          {String(n).padStart(2, "0")} / 05
        </span>
      </div>
      <h3 className="mt-5 text-lg font-bold tracking-tight text-slate-900 leading-snug">
        {title}
      </h3>
      <p className="mt-2 text-sm text-slate-500 leading-relaxed">{purpose}</p>

      <div className="mt-5 border-t border-dashed border-slate-200 pt-4">
        <div className="text-[10px] font-bold uppercase tracking-[0.12em] text-slate-400">
          Lessons
        </div>
        <ul className="mt-2 space-y-1.5">
          {lessons.map((l) => (
            <li
              key={l}
              className="text-sm text-slate-700 flex items-start gap-2"
            >
              <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#E8650A] flex-shrink-0" />
              <span>{l}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="mt-4 rounded-lg bg-[#FDECE2] px-3 py-2.5">
        <div className="text-[10px] font-bold uppercase tracking-[0.12em] text-[#E8650A]">
          Tool
        </div>
        <div className="text-sm font-semibold text-slate-900 mt-0.5">
          {tool}
        </div>
      </div>
    </article>
  );
}

function EnrollButton({ testid, className = "", size = "lg" }) {
  return (
    <Link to="/register" data-testid={testid}>
      <Button
        size={size}
        className={`ff-btn-primary bg-[#E8650A] hover:bg-[#D15A1F] font-semibold ${className}`}
      >
        Enroll for Free Trial
        <ArrowRight className="h-4 w-4 ml-1.5" />
      </Button>
    </Link>
  );
}

export default function Academy() {
  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="academy-page">
      {/* Header */}
      <header className="border-b border-slate-200 bg-white/80 backdrop-blur sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <BrandMark size="sm" withTagline />
          <Link
            to="/"
            className="text-sm font-medium text-slate-600 hover:text-[#E8650A] transition"
            data-testid="academy-back-link"
          >
            ← Back to home
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10 bg-gradient-to-b from-orange-50/70 via-white to-white" />
        <div className="max-w-4xl mx-auto px-6 pt-20 sm:pt-24 pb-16 text-center">
          <span
            className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-[#E8650A] bg-[#FDECE2] px-3 py-1 rounded-full"
            data-testid="academy-eyebrow"
          >
            <Sparkles className="h-3.5 w-3.5" /> Foodie Finder Academy
          </span>
          <h1
            className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight"
            data-testid="academy-title"
          >
            Foodie Finder <span className="text-[#E8650A]">Academy</span>
          </h1>
          <p
            className="mt-5 text-base sm:text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed"
            data-testid="academy-subtitle"
          >
            {"A calm, confidence-first learning system for food truck operators — from idea to income."}
          </p>
          <div className="mt-9 flex justify-center">
            <EnrollButton testid="academy-cta-hero" className="h-14 px-8 text-base" />
          </div>
          <p className="mt-4 text-xs text-slate-400">
            No credit card required · Cancel any time
          </p>
        </div>
      </section>

      {/* Modules */}
      <section className="max-w-6xl mx-auto px-6 pb-20">
        <div className="text-center max-w-2xl mx-auto">
          <h2
            className="text-3xl sm:text-4xl font-bold tracking-tight"
            data-testid="academy-modules-title"
          >
            5 Modules. <span className="text-[#E8650A]">Everything you need to launch and grow.</span>
          </h2>
          <p className="mt-3 text-slate-500">
            Practical, bite-sized lessons with the exact tool you need at the end of each one.
          </p>
        </div>

        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="academy-modules-grid">
          {MODULES.map((m) => (
            <ModuleCard key={m.n} {...m} />
          ))}
        </div>
      </section>

      {/* What's Included */}
      <section className="border-t border-slate-200 bg-slate-50/60">
        <div className="max-w-6xl mx-auto px-6 py-16">
          <div className="text-center">
            <span className="text-xs font-bold uppercase tracking-[0.14em] text-[#E8650A]">
              {"What's included"}
            </span>
            <h2 className="mt-2 text-2xl sm:text-3xl font-bold tracking-tight">
              Everything in one calm, focused place
            </h2>
          </div>

          <div className="mt-10 grid gap-6 sm:grid-cols-3" data-testid="academy-included-grid">
            {INCLUDED.map((item) => (
              <div
                key={item.title}
                data-testid={`academy-${item.testid}`}
                className="flex items-start gap-4 rounded-xl bg-white border border-slate-200 p-6"
              >
                <div className="h-12 w-12 rounded-xl bg-[#FDECE2] text-[#E8650A] grid place-items-center flex-shrink-0">
                  {item.icon}
                </div>
                <div>
                  <div className="font-semibold text-slate-900">{item.title}</div>
                  <div className="mt-1 text-sm text-slate-500 leading-relaxed">
                    {item.description}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="px-6 py-20">
        <div
          className="relative max-w-4xl mx-auto rounded-3xl overflow-hidden bg-slate-900 text-white px-8 sm:px-14 py-16 sm:py-20 text-center"
          data-testid="academy-bottom-cta"
        >
          {/* Orange accent bar */}
          <div className="absolute inset-x-0 top-0 h-1 bg-[#E8650A]" />
          <div className="absolute -top-24 -right-24 h-64 w-64 rounded-full bg-[#E8650A]/20 blur-3xl" />
          <div className="absolute -bottom-24 -left-24 h-64 w-64 rounded-full bg-[#E8650A]/10 blur-3xl" />

          <span className="relative inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.14em] text-[#E8650A] bg-[#E8650A]/10 px-3 py-1 rounded-full">
            <Sparkles className="h-3.5 w-3.5" /> Free trial
          </span>
          <h2 className="relative mt-5 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
            Start learning.{" "}
            <span className="text-[#E8650A]">No pressure, no locking.</span>
          </h2>
          <p className="relative mt-4 text-slate-300 max-w-xl mx-auto">
            Join food truck operators already building with confidence.
          </p>
          <div className="relative mt-8 flex justify-center">
            <EnrollButton testid="academy-cta-bottom" className="h-14 px-8 text-base" />
          </div>
        </div>
      </section>
    </div>
  );
}
